import React, { useState, useEffect, useMemo, useRef } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { API } from '../utils/helpers';
import FitTimePicker from './FitTimePicker';
import RentalBooking from './RentalBooking';
import useMobileViewport from '../utils/useMobileViewport';

// Public customer-facing booking page. Self-contained: no app theme, no
// sidebar, always light, mobile-first. Phase 1 = pay at drop-off.
// Supports deep links: /booking?package={itemID} pre-selects a package.
//
// WHICH SHOP THIS PAGE IS FOR. The address names it: /booking/<slug>. A bare
// /booking is the older single-shop link, which the backend still answers for
// as long as exactly one shop exists — the moment there are two it cannot
// guess, and says so. Every call below carries the slug, so a customer can
// never book at a shop other than the one whose link they followed.
const SHOP_SLUG = (() => {
  const m = window.location.pathname.match(/^\/booking\/([A-Za-z0-9_-]+)(?:\/rentals)?\/?$/);
  return m && m[1] !== 'manage' && m[1] !== 'rentals' ? m[1].toLowerCase() : '';
})();

// Rentals and demos have their own page (4 Oct): /booking/<slug>/rentals.
// ?tab=rentals, from the first rentals build, still lands there. The service
// page no longer offers rentals, and the rentals page offers nothing else.
const RENTALS_PAGE = /\/rentals\/?$/.test(window.location.pathname)
  || new URLSearchParams(window.location.search).get('tab') === 'rentals';

const BASE = {
  bg: '#f7f6f3',
  card: '#ffffff',
  border: '#e2e5ea',
  borderSoft: '#eef0f3',
  text: '#1a2233',
  sub: '#5b6472',
  muted: '#9aa4b2',
  disabled: '#cdd3db',
  accent: '#e67e22',
  accentTint: '#e67e2214',
  headerBg: '#0f1724',
  danger: '#c0392b',
  ok: '#27ae60',
};
let C = { ...BASE };

const input = {
  width: '100%', boxSizing: 'border-box', padding: '11px 13px',
  border: `1px solid ${C.border}`, borderRadius: 9, fontSize: 15,
  fontFamily: 'inherit', color: C.text, outline: 'none', background: '#fff',
};

const chip = {
  fontSize: 11, padding: '3px 10px', borderRadius: 12,
  background: '#f1f3f6', color: C.sub, display: 'inline-flex',
  alignItems: 'center', gap: 4,
};

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DOW = ['S','M','T','W','T','F','S'];

function fmtDate(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
}

function fmtDateShort(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function addDays(dateStr, days) {
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + days);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
}

function StepBadge({ n, active }) {
  return (
    <span style={{ width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
      background: active ? C.accent : C.borderSoft, color: active ? '#fff' : C.sub,
      fontSize: 12, fontWeight: 600, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
      {n}
    </span>
  );
}

// Card entry. Must live INSIDE <Elements> — the Stripe hooks only work in a
// child of that provider, which is why this is a separate component rather
// than inline JSX in BookingPage.
function PaymentForm({ amount, captureMethod, onPaid, onBack, busy, forFit, forRental }) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const isHold = captureMethod === 'manual';

  const pay = async () => {
    if (!stripe || !elements) return;
    setError('');
    setProcessing(true);
    const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: 'if_required',
    });
    if (stripeError) {
      setError(stripeError.message || 'Your card could not be processed. Please try another card.');
      setProcessing(false);
      return;
    }
    // Manual capture lands on 'requires_capture'; automatic on 'succeeded'.
    // Both mean the card was accepted.
    if (paymentIntent && (paymentIntent.status === 'requires_capture' || paymentIntent.status === 'succeeded')) {
      onPaid(paymentIntent.id);
      return;
    }
    setError('Payment did not complete. Please try again or call the shop.');
    setProcessing(false);
  };

  const disabled = !stripe || processing || busy;

  return (
    <>
      <div style={{ background: C.bg, borderRadius: 10, padding: '12px 14px', marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: C.text, fontWeight: 600, marginBottom: 3 }}>
          {isHold ? `A hold of $${amount.toFixed(2)} will be placed on your card` : `You'll be charged $${amount.toFixed(2)}`}
        </div>
        <div style={{ fontSize: 12, color: C.sub, lineHeight: 1.5 }}>
          {forRental
            ? (isHold
                ? "You won't be charged today. This deposit is charged a few days before pick-up, and the rest is due at pick-up."
                : 'This holds your bikes. The rest is due at pick-up.')
            : forFit
            ? (isHold
                ? "You won't be charged today. This deposit is charged a few days before your fit, and the rest is due at your fit."
                : 'This deposit holds your time. The rest is due at your fit.')
            : isHold
              ? "You won't be charged today — this is a deposit hold. We'll charge it a few days before your drop-off. Any balance is due at pickup."
              : 'This is a deposit toward your final bill. Parts and any additional service beyond the selected package are billed at pickup.'}
        </div>
      </div>

      <PaymentElement
        options={{
          // Hide the Link wallet — see PaymentPage.jsx for the full reasoning.
          // Short version: Link isn't a payment_method_type, it rides alongside
          // 'card' and drags in Instant Bank Payments and Klarna-on-Link. The
          // wallets hash is the only thing that suppresses it.
          wallets: { link: 'never' },
        }}
      />

      {error && <div style={{ fontSize: 13, color: C.danger, margin: '12px 0 0' }}>{error}</div>}

      <button onClick={pay} disabled={disabled}
        style={{ width: '100%', background: C.accent, color: '#fff', border: 'none', padding: '14px', borderRadius: 10,
          fontSize: 15, fontWeight: 600, marginTop: 16, fontFamily: 'inherit',
          cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.6 : 1 }}>
        {processing || busy ? 'Processing…' : (isHold ? `Authorize $${amount.toFixed(2)}` : `Pay $${amount.toFixed(2)}`)}
      </button>

      <button onClick={onBack} disabled={processing || busy}
        style={{ width: '100%', background: 'transparent', border: 'none', color: C.sub, fontSize: 13,
          padding: '12px 0 0', cursor: (processing || busy) ? 'default' : 'pointer', fontFamily: 'inherit' }}>
        ← Back to booking details
      </button>
    </>
  );
}

function BookingPage() {
  const [config, setConfig] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const shopRef = useRef(SHOP_SLUG);
  const [selectedItem, setSelectedItem] = useState(null); // package object, or {itemID:'custom'}
  const [selectedDate, setSelectedDate] = useState(null);
  // A fitting (a package marked Appointment) is booked to a time and a fitter
  // instead of a drop-off day: { startsAt, fitterUserId, date, free }.
  const [fitTime, setFitTime] = useState(null);
  const [fitMeta, setFitMeta] = useState(null);       // { questions, deposit, minutes }
  const [answers, setAnswers] = useState({});
  // Rentals and demos: their own page (RentalBooking). undefined while asking.
  const [rentCfg, setRentCfg] = useState(undefined);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [bike, setBike] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [confirmation, setConfirmation] = useState(null);
  // Payment step — populated only when the shop collects payment online.
  const [payment, setPayment] = useState(null); // { clientSecret, amount, captureMethod, stripeAccountId }
  const [stripePromise, setStripePromise] = useState(null);
  const calendarRef = useRef(null);

  // Picking a service is an unambiguous "I'm done with this step" signal, and
  // on mobile the package list is long enough that the calendar sits well
  // below the fold — so move them there rather than leaving them to scroll
  // past packages they've already rejected. Deferred a frame so the layout
  // has settled (focus mode may collapse the list) before measuring.
  const chooseItem = (item) => {
    setSelectedItem(item);
    setFitTime(null);
    setFitMeta(null);
    requestAnimationFrame(() => {
      setTimeout(() => {
        calendarRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 60);
    });
  };
  const deepLinked = useRef(false);
  const [focusMode, setFocusMode] = useState(false);
  const [isDesktop, setIsDesktop] = useState(typeof window !== 'undefined' && window.innerWidth >= 700);
  const isEmbed = useMemo(() => new URLSearchParams(window.location.search).get('embed') === '1', []);

  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 700);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Paint the document to match this page. The staff app paints it dark, and
  // on mobile rubber-band overscroll exposes whatever is behind the content.
  // Skipped when embedded — the host page owns its own background there.
  useEffect(() => {
    if (isEmbed) return;
    const html = document.documentElement;
    const prevHtml = html.style.background;
    const prevBody = document.body.style.background;
    html.style.background = C.bg;
    document.body.style.background = C.bg;
    return () => {
      html.style.background = prevHtml;
      document.body.style.background = prevBody;
    };
  }, [isEmbed]);

  // Force a proper mobile viewport — the staff app's index.html doesn't
  // carry one (desktop tool), which renders this page zoomed out on phones.
  useMobileViewport();

  useEffect(() => {
    fetch(`${API}/book/config${SHOP_SLUG ? `?shop=${encodeURIComponent(SHOP_SLUG)}` : ''}`)
      .then(async r => {
        const body = await r.json().catch(() => null);
        // A shop-resolution failure comes back with wording meant for the
        // customer. Anything else stays behind the generic message.
        if (!r.ok) throw Object.assign(new Error('config'), { body: r.status < 500 ? body : null });
        return body;
      })
      .then(cfg => {
        if (cfg.accentColor && /^#[0-9a-fA-F]{6}$/.test(cfg.accentColor)) {
          C = { ...BASE, accent: cfg.accentColor, accentTint: `${cfg.accentColor}14` };
        }
        // A bare /booking link resolves to a shop server-side; adopt the slug
        // it named so the booking call that follows is explicit either way.
        if (cfg.shopSlug) shopRef.current = cfg.shopSlug;
        setConfig(cfg);
      })
      .catch(err => setLoadError(err.body && err.body.error ? err.body : true));
    if (RENTALS_PAGE) {
      fetch(`${API}/book/rent/config${SHOP_SLUG ? `?shop=${encodeURIComponent(SHOP_SLUG)}` : ''}`)
        .then(r => r.json()).then(d => setRentCfg(d && d.on ? d : null)).catch(() => setRentCfg(null));
    }
  }, []);

  // Deep link: /booking?package={itemID} pre-selects and scrolls to calendar
  useEffect(() => {
    if (!config || deepLinked.current) return;
    deepLinked.current = true;
    const params = new URLSearchParams(window.location.search);
    const pkgId = params.get('package');
    if (!pkgId) return;
    if (pkgId === 'custom' && config.allowCustomBookings) {
      setSelectedItem({ itemID: 'custom' });
      setFocusMode(true);
    } else {
      const pkg = config.packages.find(p => String(p.itemID) === String(pkgId));
      if (pkg) {
        setSelectedItem(pkg);
        setFocusMode(true);
      }
    }
    }, [config]);

  const months = useMemo(() => {
    if (!config?.days) return [];
    const keys = Object.keys(config.days).sort();
    if (keys.length === 0) return [];
    const byMonth = {};
    keys.forEach(k => {
      const mk = k.slice(0, 7);
      if (!byMonth[mk]) byMonth[mk] = [];
      byMonth[mk].push(k);
    });
    return Object.keys(byMonth).sort().map(mk => {
      const [y, m] = mk.split('-').map(Number);
      const firstDow = new Date(y, m - 1, 1).getDay();
      const daysInMonth = new Date(y, m, 0).getDate();
      return { year: y, month: m, firstDow, daysInMonth };
    });
  }, [config]);

  const isCustom = selectedItem?.itemID === 'custom';
  const isFit = selectedItem?.kind === 'appointment';
  const fitWhen = fitTime ? `${fmtDateShort(fitTime.date)} · ${fitTime.label}` : '';
  const fitQuery = useMemo(() => (isFit ? { shop: shopRef.current, itemID: selectedItem.itemID } : null), [isFit, selectedItem]);

  // Estimated-ready line: only when the selected package has turnaroundDays.
  // Deliberately soft language — an estimate, not a promise.
  const estimatedReady = useMemo(() => {
    if (!selectedDate || !selectedItem || isCustom) return null;
    const days = selectedItem.turnaroundDays;
    if (!days) return null;
    return fmtDateShort(addDays(selectedDate, Number(days)));
  }, [selectedDate, selectedItem, isCustom]);

  // Creates the booking. paymentIntentId is passed only when a card was
  // collected — the backend uses it to record payment state, and to cancel or
  // refund automatically if any part of the Lightspeed write fails.
  const createBooking = async (paymentIntentId = null) => {
    try {
      const res = await fetch(`${API}${isFit ? '/book/fit/create' : '/book/create'}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerName: name.trim(),
          customerPhone: phone.trim(),
          customerEmail: email.trim(),
          itemID: selectedItem.itemID,
          ...(isFit
            ? { startsAt: fitTime.startsAt, fitterUserId: fitTime.fitterUserId, answers }
            : { dropoffDate: selectedDate }),
          bikeDescription: bike.trim(),
          notes: notes.trim(),
          ...(shopRef.current ? { shop: shopRef.current } : {}),
          ...(paymentIntentId ? { paymentIntentId } : {}),
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        // Backend has already reversed the charge/hold at this point.
        setPayment(null);
        setStripePromise(null);
        setSubmitError(data.error || 'Something went wrong. Please call the shop.');
        if (res.status === 409) {
          fetch(`${API}/book/config${shopRef.current ? `?shop=${encodeURIComponent(shopRef.current)}` : ''}`).then(r => r.json()).then(setConfig).catch(() => {});
          setSelectedDate(null);
          // A fresh picker reloads the times, so the one just taken shows as taken.
          setFitTime(null);
          setFitMeta(null);
        }
      } else {
        setConfirmation(data.booking);
        window.scrollTo(0, 0);
      }
    } catch (_) {
      setPayment(null);
      setStripePromise(null);
      setSubmitError('Something went wrong. Please call the shop.');
    }
    setSubmitting(false);
  };

  const submit = async () => {
    setSubmitError('');
    if (!selectedItem) return setSubmitError('Please choose a service.');
    if (isFit && !fitTime) return setSubmitError('Please pick a day and time.');
    if (!isFit && !selectedDate) return setSubmitError('Please pick a drop-off day.');
    if (!name.trim()) return setSubmitError('Please enter your name.');
    if (!phone.trim() && !email.trim()) return setSubmitError('Please enter a phone number or email so we can reach you.');
    if (isCustom && !notes.trim() && !bike.trim()) return setSubmitError('Please describe the problem so we know what to look at.');
    setSubmitting(true);

    // Ask the server whether this booking takes payment up front. Custom
    // ("not sure what it needs") bookings never do — there's no price yet.
    let intent = null;
    if (!isCustom) {
      try {
        const piRes = await fetch(`${API}${isFit ? '/book/fit/payment-intent' : '/book/create-payment-intent'}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            itemID: selectedItem.itemID,
            ...(isFit ? { startsAt: fitTime.startsAt } : { dropoffDate: selectedDate }),
            ...(shopRef.current ? { shop: shopRef.current } : {}),
          }),
        });
        intent = await piRes.json();
      } catch (_) {
        // Payment setup unreachable — fall through to a normal booking rather
        // than blocking the customer entirely.
        intent = null;
      }
    }

    if (intent?.paymentRequired && intent.clientSecret && config.stripePublishableKey) {
      setStripePromise(loadStripe(config.stripePublishableKey, { stripeAccount: intent.stripeAccountId }));
      setPayment(intent);
      setSubmitting(false);
      window.scrollTo(0, 0);
      return;
    }

    await createBooking();
  };

  const shell = (children) => (
    <div style={{ minHeight: '100dvh', boxSizing: 'border-box', background: isEmbed ? C.card : C.bg, padding: isEmbed ? 0 : '24px 12px', fontFamily: "'Inter', -apple-system, 'Segoe UI', sans-serif" }}>
      <div style={{ maxWidth: isEmbed ? '100%' : (isDesktop ? 620 : 440), margin: '0 auto', background: C.card, border: isEmbed ? 'none' : `1px solid ${C.border}`, borderRadius: isEmbed ? 0 : 14, overflow: 'hidden', boxShadow: isEmbed ? 'none' : '0 1px 3px rgba(15,23,36,0.08)' }}>
        <div style={{ background: C.headerBg, padding: '22px 22px 18px', textAlign: 'center' }}>
          {config?.shopLogoUrl
            ? <img src={config.shopLogoUrl} alt={(config?.shopName || '').trim()} style={{ maxHeight: 56, maxWidth: 260, objectFit: 'contain', display: 'block', margin: '0 auto' }} />
            : <div style={{ fontSize: 17, fontWeight: 600, color: '#fff', letterSpacing: '0.01em' }}>{(config?.shopName || 'Book your service').trim()}</div>}
          <div style={{ fontSize: 12, color: '#9aa4b2', marginTop: 6 }}>{RENTALS_PAGE ? 'Book a rental or demo' : 'Book your service online'}</div>
        </div>
        <div style={{ height: 3, background: C.accent }} />
        <div style={{ padding: isDesktop ? '26px 34px' : 22 }}>
          {config?.welcomeText && !confirmation && (
            <div style={{ fontSize: 13.5, color: C.sub, lineHeight: 1.6, marginBottom: 18, paddingBottom: 16, borderBottom: `1px solid ${C.borderSoft}`, whiteSpace: 'pre-line' }}>
              {config.welcomeText}
            </div>
          )}
          {children}
        </div>
      </div>
      {!isEmbed && <div style={{ textAlign: 'center', marginTop: 14, fontSize: 11, color: C.muted }}>Powered by Spinboard</div>}
      {isEmbed && <div style={{ textAlign: 'center', padding: '4px 0 14px', fontSize: 11, color: C.muted, background: C.card }}>Powered by Spinboard</div>}
    </div>
  );

  if (loadError) return shell(
    <div style={{ textAlign: 'center', padding: '30px 0' }}>
      <div style={{ fontSize: 15, color: C.text, marginBottom: 8 }}>{loadError.error || "Online booking isn't available right now."}</div>
      <div style={{ fontSize: 13, color: C.sub }}>{loadError.detail || 'Please call the shop to schedule your service.'}</div>
    </div>
  );

  if (!config) return shell(
    <div style={{ textAlign: 'center', padding: '40px 0', color: C.sub, fontSize: 14 }}>Loading…</div>
  );

  if (confirmation && confirmation.startsAt) return shell(
    <div style={{ textAlign: 'center', padding: '10px 0' }}>
      <div style={{ width: 52, height: 52, borderRadius: '50%', background: '#eafaf1', color: C.ok, fontSize: 26, lineHeight: '52px', margin: '0 auto 14px' }}>✓</div>
      <div style={{ fontSize: 18, fontWeight: 600, color: C.text, marginBottom: 6 }}>You're booked!</div>
      <div style={{ fontSize: 14, color: C.sub, lineHeight: 1.6, marginBottom: 18 }}>
        {confirmation.itemName}{confirmation.fitterName ? ` with ${confirmation.fitterName}` : ''}<br />
        <strong style={{ color: C.text }}>{confirmation.whenText}</strong>
      </div>
      <div style={{ textAlign: 'left', background: C.bg, borderRadius: 10, padding: '14px 16px', fontSize: 13, color: C.sub, lineHeight: 1.7 }}>
        <strong style={{ color: C.text }}>What to bring</strong><br />
        Your bike, cycling shoes and pedals, and the shorts you ride in.
        {confirmation.minutes ? ` Plan on about ${confirmation.minutes % 60 === 0 ? `${confirmation.minutes / 60} hour${confirmation.minutes === 60 ? '' : 's'}` : `${confirmation.minutes} minutes`}.` : ''}
        {' '}{payment
          ? (payment.captureMethod === 'manual'
              ? `A $${Number(payment.amount).toFixed(2)} deposit is held on your card and charged a few days before; the rest is due at your fit.`
              : `$${Number(payment.amount).toFixed(2)} deposit received; the rest is due at your fit.`)
          : 'Payment is due at your fit.'}
        <br /><br />
        We'll text you a reminder the day before. The link in your confirmation lets you reschedule or cancel.
      </div>
    </div>
  );

  if (confirmation) return shell(
    <div style={{ textAlign: 'center', padding: '10px 0' }}>
      <div style={{ width: 52, height: 52, borderRadius: '50%', background: '#eafaf1', color: C.ok, fontSize: 26, lineHeight: '52px', margin: '0 auto 14px' }}>✓</div>
      <div style={{ fontSize: 18, fontWeight: 600, color: C.text, marginBottom: 6 }}>You're booked!</div>
      <div style={{ fontSize: 14, color: C.sub, lineHeight: 1.6, marginBottom: 18 }}>
        {confirmation.itemName}<br />
        <strong style={{ color: C.text }}>{fmtDate(confirmation.dropoffDate)}</strong>
      </div>
      <div style={{ textAlign: 'left', background: C.bg, borderRadius: 10, padding: '14px 16px', fontSize: 13, color: C.sub, lineHeight: 1.7 }}>
        <strong style={{ color: C.text }}>What happens next</strong><br />
        Bring your bike in on your drop-off day. {payment
          ? (payment.captureMethod === 'manual'
              ? `A hold of $${Number(payment.amount).toFixed(2)} is on your card as a deposit — we'll charge it a few days before your drop-off. Any balance is due at pickup. `
              : `Deposit of $${Number(payment.amount).toFixed(2)} received — thank you! Any balance is due at pickup. `)
          : (Number(confirmation.itemPrice) > 0 ? `Payment of $${Number(confirmation.itemPrice).toFixed(2)} is due at pickup. ` : '')}If we find anything beyond the selected service, we'll reach out before doing any additional work.
      </div>
    </div>
  );

  if (payment && stripePromise) return shell(
    <div>
      <div style={{ fontSize: 16, fontWeight: 600, color: C.text, marginBottom: 4 }}>Confirm and pay</div>
      <div style={{ fontSize: 13, color: C.sub, marginBottom: 16 }}>
        {(selectedItem.displayName || selectedItem.name)} · {isFit ? fitWhen : fmtDateShort(selectedDate)}
      </div>
      <Elements
        stripe={stripePromise}
        options={{ clientSecret: payment.clientSecret, appearance: { theme: 'stripe', variables: { colorPrimary: C.accent } } }}>
        <PaymentForm
          amount={payment.amount}
          captureMethod={payment.captureMethod}
          forFit={isFit}
          busy={submitting}
          onPaid={(intentId) => { setSubmitting(true); createBooking(intentId); }}
          onBack={() => { setPayment(null); setStripePromise(null); }}
        />
      </Elements>
      {submitting && (
        <div style={{ fontSize: 12, color: C.sub, textAlign: 'center', marginTop: 12 }}>Finalizing your booking…</div>
      )}
    </div>
  );

  if (RENTALS_PAGE) return shell(
    rentCfg === undefined
      ? <div style={{ fontSize: 13, color: C.sub, textAlign: 'center', padding: '20px 0' }}>Loading…</div>
      : rentCfg === null
        ? <div style={{ fontSize: 14, color: C.sub, textAlign: 'center', padding: '20px 0', lineHeight: 1.6 }}>Rentals and demos can't be booked online right now.{config.shopPhone ? <> Please call us at <strong style={{ color: C.text }}>{config.shopPhone}</strong>.</> : ' Please call the shop.'}</div>
        : <RentalBooking C={C} shop={shopRef.current} cfg={rentCfg} paymentMode={config.paymentMode}
            stripePublishableKey={config.stripePublishableKey} PaymentForm={PaymentForm} input={input} StepBadge={StepBadge} />
  );

  // The note under the packages: a fitting's own once one is picked, else the
  // service drop-off note.
  const noteText = isFit ? (config.fitDisclaimerText || '') : (config.disclaimerText || '');

  return shell(
    <>
      {/* 1 · Service */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <StepBadge n="1" active={!selectedItem} />
        <span style={{ fontSize: 14, fontWeight: 600, color: C.text }}>Choose your service</span>
      </div>
      {config.packages.filter(pkg => !focusMode || selectedItem?.itemID === pkg.itemID).map(pkg => {
        const sel = selectedItem?.itemID === pkg.itemID;
        return (
          <div key={pkg.itemID} onClick={() => chooseItem(pkg)}
            style={{ border: sel ? `2px solid ${C.accent}` : `1px solid ${C.border}`, borderRadius: 12,
              padding: sel ? '13px 15px' : '14px 16px', marginBottom: 10, cursor: 'pointer',
              background: sel ? C.accentTint : '#fff' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <span style={{ fontSize: 15, fontWeight: 600, color: C.text }}>{pkg.displayName || pkg.name}</span>
              <span style={{ fontSize: 16, fontWeight: 600, color: C.text, fontVariantNumeric: 'tabular-nums' }}>${pkg.price.toFixed(0)}</span>
            </div>
            {pkg.subtitle && <div style={{ fontSize: 12, color: C.muted, marginTop: 3 }}>{pkg.subtitle}</div>}
            {pkg.blurb && <div style={{ fontSize: 12.5, color: C.sub, marginTop: 7, lineHeight: 1.55 }}>{pkg.blurb}</div>}
            <div style={{ display: 'flex', gap: 6, marginTop: 9, flexWrap: 'wrap' }}>
              {pkg.kind === 'appointment' ? (
                <>
                  <span style={{ ...chip, background: C.accentTint, color: C.accent, fontWeight: 600 }}>Appointment</span>
                  {pkg.minutes && <span style={chip}>🕐 {pkg.minutes % 60 === 0 ? `${pkg.minutes / 60} hour${pkg.minutes === 60 ? '' : 's'}` : `${pkg.minutes} minutes`}</span>}
                </>
              ) : (
                <>
                  {pkg.turnaround && <span style={chip}>🕐 {pkg.turnaround}</span>}
                  <span style={chip}>Parts extra</span>
                </>
              )}
            </div>
          </div>
        );
      })}
      {selectedItem && (config.packages.length > 1 || config.allowCustomBookings) && (
        <button onClick={() => setFocusMode(v => !v)}
          style={{ background: 'transparent', border: 'none', color: C.sub, fontSize: 12.5, cursor: 'pointer', padding: '2px 0 10px', fontFamily: 'inherit', textDecoration: 'underline', textUnderlineOffset: 3 }}>
          {focusMode ? 'See all services' : 'Hide other services'}
        </button>
      )}
      {config.allowCustomBookings && (!focusMode || isCustom) && (
        <div onClick={() => chooseItem({ itemID: 'custom' })}
          style={{ border: isCustom ? `2px solid ${C.accent}` : `1px dashed ${C.disabled}`, borderRadius: 12,
            padding: isCustom ? '12px 15px' : '13px 16px', marginBottom: 10, cursor: 'pointer',
            background: isCustom ? C.accentTint : '#fff' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <span style={{ fontSize: 17, lineHeight: '20px' }}>🔍</span>
            <div>
              <span style={{ fontSize: 14, fontWeight: 600, color: C.text }}>Not sure what it needs?</span>
              <div style={{ fontSize: 12, color: C.sub, marginTop: 2, lineHeight: 1.5 }}>
                Book a drop-off and describe the problem. We'll assess it and confirm with you before any work.
              </div>
            </div>
          </div>
        </div>
      )}
      {config.packages.length === 0 && !config.allowCustomBookings && (
        <div style={{ fontSize: 13, color: C.sub, marginBottom: 8 }}>No services are currently available for online booking. Please call the shop.</div>
      )}

      {noteText && (
        <div style={{ background: '#faf9f5', border: `1px solid ${C.borderSoft}`, borderRadius: 10, padding: '10px 14px', margin: '4px 0 6px' }}>
          <span style={{ fontSize: 12, color: C.sub, lineHeight: 1.6, whiteSpace: 'pre-line' }}>{noteText}</span>
        </div>
      )}

      {/* 2 · Day (or day and time, for a fitting) */}
      <div ref={calendarRef} style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '22px 0 12px' }}>
        <StepBadge n="2" active={!!selectedItem && (isFit ? !fitTime : !selectedDate)} />
        <span style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{isFit ? 'Pick a day and time' : 'Pick a drop-off day'}</span>
      </div>
      {isFit && (
        <>
          <div style={{ fontSize: 12.5, color: C.sub, margin: '-4px 0 12px', lineHeight: 1.5 }}>
            {selectedItem.displayName || selectedItem.name}{selectedItem.minutes ? ` takes about ${selectedItem.minutes % 60 === 0 ? `${selectedItem.minutes / 60} hour${selectedItem.minutes === 60 ? '' : 's'}` : `${selectedItem.minutes} minutes`}` : ''}. Choose who you'd like, or the first available.
          </div>
          <FitTimePicker key={selectedItem.itemID} query={fitQuery} C={C} value={fitTime} onChange={setFitTime} onMeta={setFitMeta} />
        </>
      )}
      {!isFit && months.map(({ year, month, firstDow, daysInMonth }) => (
        <div key={`${year}-${month}`} style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 6 }}>{MONTH_NAMES[month - 1]} {year}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginBottom: 4 }}>
            {DOW.map((d, i) => <span key={i} style={{ fontSize: 11, color: C.muted, textAlign: 'center' }}>{d}</span>)}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
            {Array.from({ length: firstDow }).map((_, i) => <span key={`pad-${i}`} />)}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const key = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
              const state = config.days[key];
              const sel = selectedDate === key;
              const base = { fontSize: 13, textAlign: 'center', padding: '8px 0', borderRadius: 8, fontVariantNumeric: 'tabular-nums' };
              if (sel) return <span key={key} style={{ ...base, background: C.accent, color: '#fff', fontWeight: 600, cursor: 'pointer' }} onClick={() => setSelectedDate(null)}>{day}</span>;
              if (state === 'available') return <span key={key} style={{ ...base, border: `1px solid ${C.border}`, color: C.text, cursor: 'pointer' }} onClick={() => setSelectedDate(key)}>{day}</span>;
              if (state === 'full') return <span key={key} style={{ ...base, color: C.muted, textDecoration: 'line-through' }}>{day}</span>;
              return <span key={key} style={{ ...base, color: C.disabled }}>{day}</span>;
            })}
          </div>
        </div>
      ))}
      {!isFit && selectedDate && estimatedReady && (
        <div style={{ fontSize: 11.5, color: C.muted, margin: '0 0 4px', lineHeight: 1.5 }}>
          📅 Drop off {fmtDateShort(selectedDate)} — estimated ready {estimatedReady}. Estimate only; we'll confirm timing when we see the bike.
        </div>
      )}

      {/* 3 · Info */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '22px 0 12px' }}>
        <StepBadge n="3" active={!!selectedItem && (isFit ? !!fitTime : !!selectedDate)} />
        <span style={{ fontSize: 14, fontWeight: 600, color: C.text }}>Your info</span>
      </div>
      <input style={{ ...input, marginBottom: 8 }} placeholder="Full name" value={name} onChange={e => setName(e.target.value)} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 4 }}>
        <input style={input} placeholder="Phone" inputMode="tel" value={phone} onChange={e => setPhone(e.target.value)} />
        <input style={input} placeholder="Email" inputMode="email" value={email} onChange={e => setEmail(e.target.value)} />
      </div>
      <div style={{ fontSize: 11, color: C.muted, marginBottom: 8, lineHeight: 1.4 }}>
        We'll send your booking confirmation by text or email. Message and data rates may apply.
      </div>
      <input style={{ ...input, marginBottom: 8 }} placeholder="Your bike — e.g. Trek Domane SL6, red" value={bike} onChange={e => setBike(e.target.value)} />
      {isFit && fitMeta && (fitMeta.questions || []).filter(q => q.label).map(q => (
        <input key={q.id} style={{ ...input, marginBottom: 8 }} placeholder={q.label}
          value={answers[q.id] || ''} onChange={e => setAnswers(a => ({ ...a, [q.id]: e.target.value }))} />
      ))}
      <textarea style={{ ...input, height: 64, resize: 'none', marginBottom: 16 }}
        placeholder={isCustom ? "What's going on with the bike? The more detail, the better." : isFit ? 'Anything else we should know? Bring your shoes and pedals.' : 'Anything we should know?'}
        value={notes} onChange={e => setNotes(e.target.value)} />

      {/* Summary + submit */}
      {isFit && fitTime && (
        <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 13, marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span style={{ fontSize: 13, color: C.sub }}>{selectedItem.displayName || selectedItem.name} · {fitWhen}</span>
            <span style={{ fontSize: 15, fontWeight: 600, color: C.text }}>${selectedItem.price.toFixed(2)}</span>
          </div>
          <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
            {config.paymentMode && config.paymentMode !== 'off'
              ? (config.fitDeposit && config.fitDeposit < selectedItem.price
                  ? `A $${Number(config.fitDeposit).toFixed(2)} deposit holds your time. The balance is due at your fit.`
                  : 'Paid when you book, to hold your time.')
              : 'Payment due at your fit.'}
            {fitMeta && fitMeta.freeCancelHours ? ` Need to change it? Reschedule or cancel up to ${fitMeta.freeCancelHours} hours before${config.paymentMode && config.paymentMode !== 'off' ? ' for a full refund' : ''}.` : ''}
          </div>
        </div>
      )}
      {!isFit && selectedItem && selectedDate && (
        <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 13, marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span style={{ fontSize: 13, color: C.sub }}>
              {isCustom ? 'Service assessment' : (selectedItem.displayName || selectedItem.name)} · {fmtDateShort(selectedDate)}
            </span>
            {!isCustom && <span style={{ fontSize: 15, fontWeight: 600, color: C.text }}>${selectedItem.price.toFixed(2)}</span>}
          </div>
          <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
            {isCustom
              ? "We'll assess your bike and confirm pricing with you before doing any work."
              : config.paymentMode === 'charge'
                ? 'Paid as a deposit when you book. Parts and additional service are billed at pickup.'
                : config.paymentMode === 'antiflake'
                  ? 'Held as a deposit when you book. Parts and additional service are billed at pickup.'
                  : 'Payment due at pickup. Parts and additional service extra.'}
          </div>
        </div>
      )}
      {!isFit && config.cancellationPolicyText && (
        <div style={{ fontSize: 11.5, color: C.muted, lineHeight: 1.5, marginBottom: 12, whiteSpace: 'pre-line' }}>{config.cancellationPolicyText}</div>
      )}
      {submitError && (
        <div style={{ fontSize: 13, color: C.danger, marginBottom: 10 }}>{submitError}</div>
      )}
      <button onClick={submit} disabled={submitting}
        style={{ width: '100%', background: C.accent, color: '#fff', border: 'none', padding: '14px', borderRadius: 10,
          fontSize: 15, fontWeight: 600, cursor: submitting ? 'default' : 'pointer', opacity: submitting ? 0.6 : 1, fontFamily: 'inherit' }}>
        {submitting ? 'Booking…' : (!isCustom && config.paymentMode && config.paymentMode !== 'off' ? 'Continue to payment' : 'Book my service')}
      </button>
    </>
  );
}

export default BookingPage;